import type { DataProvider, Identifier } from "ra-core";

import type { WorkItem } from "../types";

// «Hva er neste steg?» åpnes når en oppgave koblet til en mulighet blir
// fullført (kap. 7.1), uansett hvor den fullføres: oppgavelista, «I dag»,
// prosjekttavla eller muligheten selv. Datalaget sender en hendelse etter
// lagringen, og NextStepPrompt lytter.
export const TASK_COMPLETED_EVENT = "mimin:deal-task-completed";

export type TaskCompletedDetail = {
  dealId: Identifier;
  taskId: Identifier;
};

const emit = (detail: TaskCompletedDetail) => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(TASK_COMPLETED_EVENT, { detail }));
};

const justCompleted = (
  next: Partial<WorkItem> | undefined,
  previous: Partial<WorkItem> | undefined,
) => !!next?.completed_at && !previous?.completed_at && next.deal_id != null;

export const withTaskCompletionEvents = <T extends DataProvider>(
  dataProvider: T,
): T => ({
  ...dataProvider,
  update: async (resource, params) => {
    const result = await dataProvider.update(resource, params);
    if (
      resource === "work_items" &&
      justCompleted(result.data as Partial<WorkItem>, params.previousData)
    ) {
      emit({
        dealId: (result.data as WorkItem).deal_id!,
        taskId: result.data.id,
      });
    }
    return result;
  },
  updateMany: async (resource, params) => {
    const result = await dataProvider.updateMany(resource, params);
    // Fullføring skjer med completed_at eller med statusen som fullfører
    // oppgaven (section_id); da setter databasen completed_at.
    if (
      resource === "work_items" &&
      (params.data?.completed_at || params.data?.section_id != null)
    ) {
      // Massefullføring: bare én forespørsel om neste steg per mulighet.
      const { data } = await dataProvider.getMany<WorkItem>("work_items", {
        ids: params.ids,
      });
      const seen = new Set<string>();
      for (const task of data) {
        if (
          !task.completed_at ||
          task.deal_id == null ||
          seen.has(String(task.deal_id))
        )
          continue;
        seen.add(String(task.deal_id));
        emit({ dealId: task.deal_id, taskId: task.id });
      }
    }
    return result;
  },
});

// Muligheter som allerede har «Hva er neste steg?» åpen. Fullfører vi et steg
// fra dialogen, skal den ikke åpnes en gang til.
export const activeNextStepDialogs = new Set<string>();
